import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { type User, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import type { RolUsuario, CustomClaims } from '../types';
import {
  loginEmailPassword,
  logout as logoutService,
} from '../services/authService';
import { Analytics } from '../services/analytics';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  role: RolUsuario | null;
  claims: CustomClaims | null;
  isAdmin: boolean;
  isSupervisor: boolean;
  isMedico: boolean;
  isNotificador: boolean;
  /** Rol de solo lectura: pantallas de TV y trazabilidad, sin operar nada. */
  isVisor: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshClaims: () => Promise<void>;
  hasAccessToSala: (salaId: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<CustomClaims | null>(null);
  const [loading, setLoading] = useState(true);

  const loadClaims = useCallback(async (firebaseUser: User, forceRefresh = false) => {
    try {
      // forceRefresh=true en sign-in fresco o cuando se llama refreshClaims
      // manualmente. En navegación interna usa cache (Firebase refresca solo
      // cerca del expiry de ~1h).
      const tokenResult = await firebaseUser.getIdTokenResult(forceRefresh);
      const c = tokenResult.claims;
      if (c.role) {
        setClaims({
          role: c.role as RolUsuario,
          salaIds: (c.salaIds as string[]) || [],
          colegiadoId: (c.colegiadoId as string) || '',
          especialidad: (c.especialidad as string) || '',
          activo: c.activo !== false,
        });
      } else {
        setClaims(null);
      }
    } catch {
      // Un refresco que falla NO es una pérdida de permisos. Antes se hacía
      // `setClaims(null)`, y como el refresco forzado corre cada 5 min y al
      // volver a la pestaña, cualquier corte de red momentáneo dejaba al
      // usuario sin rol: ProtectedRoute lo sacaba de la pantalla con "No tiene
      // permisos para acceder a esta seccion" (issue #43). Se conserva lo que
      // ya se tenía; los permisos solo cambian cuando el servidor los cambia,
      // y el siguiente refresco lo traerá.
      console.warn('[auth] no se pudo refrescar el token; se conservan los permisos vigentes');
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadClaims(firebaseUser);
      } else {
        setUser(null);
        setClaims(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [loadClaims]);

  // Heartbeat de presencia del médico (global — cubre todas las páginas, incl.
  // la videoconsulta). El backend usa `ultimoHeartbeat` para contar médicos EN
  // LÍNEA de verdad (el estado 'disponible' queda stale si cierran el navegador).
  // Va en `medicos_latido/{uid}` y no en `medicos/{uid}`: ahí cada latido
  // disparaba los triggers de `medicos` y lo releía cada pantalla que lista médicos.
  useEffect(() => {
    if (!user || claims?.role !== 'medico') return;
    const uid = user.uid;
    const beat = () => {
      void setDoc(doc(db, 'medicos_latido', uid), { ultimoHeartbeat: serverTimestamp() }).catch(() => { /* best-effort */ });
    };
    beat();
    const id = setInterval(beat, 45_000);
    return () => clearInterval(id);
  }, [user, claims?.role]);

  // Los custom claims (role, salaIds, activo, etc.) solo llegan en un ID token
  // nuevo. Si un admin cambia el rol/salas de este usuario mientras ya tiene
  // sesión abierta, el cambio no se refleja hasta el próximo refresh natural
  // (~1h de Firebase). Forzamos un refresh silencioso periódico y al volver a
  // la pestaña para que esos cambios apliquen sin exigir cerrar sesión.
  useEffect(() => {
    if (!user) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void loadClaims(user, true);
    };
    document.addEventListener('visibilitychange', onVisible);
    const id = setInterval(() => void loadClaims(user, true), 5 * 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(id);
    };
  }, [user, loadClaims]);

  const login = async (email: string, password: string) => {
    await loginEmailPassword(email, password);
    Analytics.loginSuccess('email_password');
  };

  const logout = async () => {
    await logoutService();
    setUser(null);
    setClaims(null);
  };

  const refreshClaims = async () => {
    if (user) {
      // Forzar refresh — actualiza el ID token con los claims más recientes
      // del servidor (ej. tras un cambio de salas hecho por admin).
      await loadClaims(user, true);
    }
  };

  const role = claims?.role || null;

  const hasAccessToSala = (salaId: string): boolean => {
    if (!claims?.activo) return false;
    if (claims.salaIds.includes('*')) return true;
    return claims.salaIds.includes(salaId);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        role,
        claims,
        isAdmin: role === 'admin',
        isSupervisor: role === 'supervisor',
        isMedico: role === 'medico',
        isNotificador: role === 'notificador',
        isVisor: role === 'visor',
        login,
        logout,
        refreshClaims,
        hasAccessToSala,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
